(() => {
  const wrapperSelector = ".candyrack-slider-cart-offers-wrapper";
  const headerSelector = ".candyrack-slider-cart-offers-header-wrapper";
  const listSelector = ".candyrack-slider-cart-offers-list";
  const itemSelector = ".candyrack-slider-cart-offer-item-wrapper";
  const navClass = "eyesnug-cart-offers-nav";
  const railStates = new WeakMap();
  const offerButtonSelector = "[data-eyesnug-candyrack-product-id]";
  const offerApiBase = "https://candyrack.digismoothie.app/api/candyrack/offers";
  const offerWarmFreshness = 4500;
  const warmedOffers = new Map();
  const pendingButtons = new Set();

  const getOfferContext = () => {
    const button = document.querySelector(offerButtonSelector);
    if (!button) return null;

    const form = button.form || button.closest("form");
    const variantInput = form?.querySelector('[name="id"]');
    const productId = button.dataset.eyesnugCandyrackProductId;
    const shop = button.dataset.eyesnugCandyrackShop;
    const locale = button.dataset.eyesnugCandyrackLocale || "en";
    const variantId = variantInput?.value;

    if (!productId || !shop || !variantId) return null;
    return { productId, shop, locale, variantId };
  };

  const warmCandyRackOffers = () => {
    const context = getOfferContext();
    if (!context) return;

    const warmKey = `${context.productId}:${context.variantId}:${context.locale}`;
    const previousWarm = warmedOffers.get(warmKey);
    if (previousWarm && Date.now() - previousWarm.startedAt < offerWarmFreshness) return;

    const query = new URLSearchParams({
      shop: context.shop,
      product: context.productId,
      product_variant: context.variantId,
      lang: context.locale,
    });

    /*
     * Candy Rack requests this same small, public response after the customer
     * clicks Add to cart. A matching, anonymous fetch preload lets the browser
     * reuse the in-flight or completed response after clear shopping intent.
     * It does not add an item, open the cart, replace window.fetch, or
     * bypass Candy Rack's own request and lifecycle.
     */
    previousWarm?.preload.remove();
    const preload = document.createElement("link");
    preload.rel = "preload";
    preload.as = "fetch";
    preload.crossOrigin = "anonymous";
    preload.fetchPriority = "low";
    preload.href = `${offerApiBase}?${query.toString()}`;
    document.head.append(preload);
    warmedOffers.set(warmKey, { preload, startedAt: Date.now() });
  };

  const warmFromProductIntent = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest("product-form") || target.closest(offerButtonSelector)) {
      warmCandyRackOffers();
    }
  };

  const clearPendingButton = (button) => {
    button.classList.remove("loading");
    button.removeAttribute("aria-busy");
    pendingButtons.delete(button);
  };

  const showImmediateAddFeedback = (event) => {
    const button = event.detail?.clonedButton;
    if (!(button instanceof Element) || !button.matches(offerButtonSelector)) return;

    button.classList.add("loading");
    button.setAttribute("aria-busy", "true");
    pendingButtons.add(button);
    window.setTimeout(() => clearPendingButton(button), 12000);
  };

  if (document.querySelector(offerButtonSelector)) {
    document.addEventListener("pointerover", warmFromProductIntent, { passive: true });
    document.addEventListener("touchstart", warmFromProductIntent, { passive: true });
    document.addEventListener("focusin", warmFromProductIntent);
    document.addEventListener("change", warmFromProductIntent);
    document.addEventListener("addToCartButtonClicked", showImmediateAddFeedback);
    document.addEventListener("candyrack-closed", () => {
      pendingButtons.forEach(clearPendingButton);
    });
  }

  const arrowSvg = (direction) => {
    const path = direction === "previous" ? "M15 18l-6-6 6-6" : "M9 6l6 6-6 6";

    return `
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="${path}" fill="none" stroke="currentColor" stroke-width="2"
          stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    `;
  };

  const makeButton = (direction) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = `eyesnug-cart-offers-nav__button eyesnug-cart-offers-nav__button--${direction}`;
    button.setAttribute("aria-label", direction === "previous" ? "Previous offer" : "Next offer");
    button.innerHTML = arrowSvg(direction);
    return button;
  };

  const prioritizeThumbnails = (list) => {
    list.querySelectorAll(".candyrack-slider-cart-offer-item-image").forEach((image, index) => {
      image.decoding = "async";

      /* The mobile rail shows two cards, so defer every thumbnail beyond them. */
      if (index < 2) {
        image.loading = "eager";
        image.fetchPriority = index === 0 ? "high" : "auto";
      } else {
        image.loading = "lazy";
        image.fetchPriority = "low";
      }
    });
  };

  const initializeRail = (wrapper) => {
    const header = wrapper.querySelector(headerSelector);
    const list = wrapper.querySelector(listSelector);

    if (!header || !list) return;

    prioritizeThumbnails(list);

    const existingState = railStates.get(list);
    if (existingState) {
      window.requestAnimationFrame(existingState.updateButtons);
      return;
    }

    /* Recover cleanly if Candy Rack retained old controls while replacing the rail. */
    header.querySelector(`.${navClass}`)?.remove();

    const previousButton = makeButton("previous");
    const nextButton = makeButton("next");
    const nav = document.createElement("div");
    nav.className = navClass;
    nav.setAttribute("role", "group");
    nav.setAttribute("aria-label", "Browse additional offers");
    nav.append(previousButton, nextButton);
    header.append(nav);

    const cardStep = () => {
      const card = list.querySelector(itemSelector);
      if (!card) return list.clientWidth;

      const styles = window.getComputedStyle(list);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      return card.getBoundingClientRect().width + gap;
    };

    const updateButtons = () => {
      const maximum = Math.max(0, list.scrollWidth - list.clientWidth);
      previousButton.disabled = list.scrollLeft <= 2;
      nextButton.disabled = list.scrollLeft >= maximum - 2;
    };

    previousButton.addEventListener("click", () => {
      list.scrollBy({ left: -cardStep(), behavior: "smooth" });
    });

    nextButton.addEventListener("click", () => {
      list.scrollBy({ left: cardStep(), behavior: "smooth" });
    });

    let animationFrame = 0;
    list.addEventListener(
      "scroll",
      () => {
        window.cancelAnimationFrame(animationFrame);
        animationFrame = window.requestAnimationFrame(updateButtons);
      },
      { passive: true }
    );

    if ("ResizeObserver" in window) {
      new ResizeObserver(updateButtons).observe(list);
    }

    railStates.set(list, { updateButtons });
    window.requestAnimationFrame(updateButtons);
  };

  const initializeAllRails = () => {
    document.querySelectorAll(wrapperSelector).forEach(initializeRail);
  };

  const initializeFromMutations = (records) => {
    const wrappers = new Set();

    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;

        if (node.matches(wrapperSelector)) wrappers.add(node);

        const containingWrapper = node.closest(wrapperSelector);
        if (containingWrapper) wrappers.add(containingWrapper);

        node.querySelectorAll(wrapperSelector).forEach((wrapper) => wrappers.add(wrapper));
      });
    });

    wrappers.forEach(initializeRail);
  };

  let observer;
  let fallbackTimer;
  const cartIntentEvents = ["pointerdown", "touchstart", "keydown"];

  const stopListeningForCartIntent = () => {
    cartIntentEvents.forEach((eventName) => {
      document.removeEventListener(eventName, initializeFromCartIntent, true);
    });
  };

  const startWatchingCartOffers = () => {
    if (observer) return;

    initializeAllRails();

    const cartDrawer = document.querySelector("cart-drawer");
    if (!cartDrawer) return;

    observer = new MutationObserver(initializeFromMutations);
    observer.observe(cartDrawer, { childList: true, subtree: true });
    stopListeningForCartIntent();

    if (fallbackTimer) {
      window.clearTimeout(fallbackTimer);
      fallbackTimer = 0;
    }
  };

  function initializeFromCartIntent(event) {
    if (
      event.type === "keydown" &&
      event.key !== "Enter" &&
      event.key !== " "
    ) {
      return;
    }

    const cartIntent = event.target.closest?.(
      '.cart-open-trigger, [name="add"], form[action^="/cart/add"] [type="submit"], cart-drawer'
    );

    if (cartIntent) startWatchingCartOffers();
  }

  /*
   * Candy Rack's cart rail is not visible until a customer interacts with the
   * storefront. Avoid processing unrelated app mutations during the product
   * page's critical render, then begin watching before that first interaction
   * can open or update the cart drawer.
   */
  cartIntentEvents.forEach((eventName) => {
    document.addEventListener(eventName, initializeFromCartIntent, {
      capture: true,
      passive: eventName !== "keydown",
    });
  });

  document.addEventListener("shopify:section:load", startWatchingCartOffers);
  fallbackTimer = window.setTimeout(startWatchingCartOffers, 10000);

  window.addEventListener(
    "pagehide",
    () => {
      if (fallbackTimer) window.clearTimeout(fallbackTimer);
      warmedOffers.forEach(({ preload }) => preload.remove());
      warmedOffers.clear();
      pendingButtons.forEach(clearPendingButton);
      stopListeningForCartIntent();
      observer?.disconnect();
    },
    { once: true }
  );
})();
